import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { faPlus, faShip, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  App,
  Button,
  Checkbox,
  Descriptions,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Table,
  Tag,
} from 'antd';
import { styled } from 'styled-components';
import { useLocations } from '@/api/Locations';
import { useShippingManifest, useVesselPositions } from '@/api/Vessel';
import { vessel } from '@/api/Vessel/Vessel';
import { useTranslate } from '@/translations';
import { FerryingPeriod, ReadinessCheck, VesselPortCall } from '@/types';
import { faPlay, Icon, Map, TooltipIcon } from '@/ui/components';
import { timeConverter } from '@/utils/helpers';

const DetailFrame = styled.div<{ $width?: number }>`
  box-sizing: border-box;
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
  max-width: 100%;
  min-width: 0;
  position: sticky;
  left: 0;
  white-space: normal;
  container-type: inline-size;
  container-name: port-call-detail;
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  align-items: start;
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const UpcomingGrid = styled(Grid)`
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;

  @container port-call-detail (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container port-call-detail (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const InPortGrid = styled(Grid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;

  > * {
    height: 100%;
  }

  @container port-call-detail (max-width: 680px) {
    grid-template-columns: 1fr;

    > :nth-child(3) {
      grid-column: auto;
    }
  }
`;

const ThreeColumnGrid = styled(Grid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;

  @container port-call-detail (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @container port-call-detail (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const CompletedGrid = styled(ThreeColumnGrid)`
  > :nth-child(4) {
    grid-column: 1 / 3;
  }

  @container port-call-detail (max-width: 1400px) {
    > :nth-child(4) {
      grid-column: auto;
    }
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;

  > :last-child {
    flex: 1 1 auto;
  }
`;

const Panel = styled.section`
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  padding: 12px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
`;

const PanelTitle = styled.h4`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 4px;
`;

const Subtitle = styled.div`
  margin: 0 0 10px;
  color: #667085;
  font-size: 18px;
`;

const FieldList = styled.dl`
  margin: 0;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(108px, 42%) 1fr;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;

  dt {
    margin: 0;
    color: #667085;
  }

  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const Hint = styled.p`
  margin: 10px 0 0;
  color: #667085;
  font-size: 12px;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 20px 8px;
  color: #667085;
  text-align: center;
`;

const Banner = styled.div<{ $ok?: boolean }>`
  margin: 8px 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ $ok }) => ($ok ? '#f6ffed' : '#fff7e6')};
  color: ${({ $ok }) => ($ok ? '#389e0d' : '#d46b08')};
  font-size: 13px;
  text-align: center;
`;

const VesselHead = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
`;

const VesselMark = styled.div`
  display: flex;
  flex: 0 0 72px;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 48px;
  border-radius: 6px;
  background: #e8f1fb;
  color: #1677ff;
`;

const MapFrame = styled.div`
  height: 150px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 8px;

  .h-100 {
    height: 100%;
  }

  .w-100 {
    width: 100%;
  }
`;

const Track = styled.div`
  position: relative;
  height: 28px;
  margin: 8px 0 12px;
  border-radius: 6px;
  background: #f2f4f7;
`;

const TrackBlock = styled.div`
  position: absolute;
  top: 4px;
  height: 20px;
  overflow: hidden;
  padding: 0 4px;
  border-radius: 4px;
  background: #1677ff;
  color: #fff;
  font-size: 11px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActivityList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
`;

const ActivityItem = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 9.5rem) 10px minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  margin-top: 4px;
  border-radius: 50%;
  background: #1677ff;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
`;

type DetailProps = {
  row: VesselPortCall;
  canOperate: boolean;
  onAddPeriod: () => void;
  onAutoCreate: () => void;
  onDeletePeriod: (period: FerryingPeriod) => void;
  onToggleCheck: (checkId: number, complete: boolean) => void;
  onStart: () => void;
  onClose: () => void;
  onOpenManifest?: (vesselName?: string) => void;
  pending?: {
    savePeriod?: boolean;
    deletePeriod?: boolean;
    start?: boolean;
    close?: boolean;
    check?: boolean;
  };
};

const useFitWidth = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [width, setWidth] = useState<number>();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const scroller = node.closest(
      '.ant-table-content, .ant-table-body',
    ) as HTMLElement | null;
    const target = scroller ?? node.parentElement;
    if (!target) return;
    const update = () => setWidth(Math.max(target.clientWidth - 32, 0));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(target);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref]);

  return width;
};

const text = (value?: string | number | null) =>
  value === undefined || value === null || value === '' ? '—' : value;

const windowText = (from?: string, to?: string) => {
  if (!from && !to) return '—';
  return `${timeConverter(from) || '—'} – ${timeConverter(to) || '—'}`;
};

const durationText = (from?: string, to?: string) => {
  if (!from || !to) return '—';
  const minutes = dayjs(to).diff(dayjs(from), 'minute');
  const sign = minutes < 0 ? '-' : '';
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const rest = absolute % 60;
  if (hours && rest) return `${sign}${hours}h ${rest}m`;
  if (hours) return `${sign}${hours}h`;
  return `${sign}${rest}m`;
};

const planQuantity = (row: VesselPortCall) => row.plannedVehicleQty ?? 0;

const earliest = (values: Array<string | undefined>) => {
  const present = values.filter((value): value is string => Boolean(value));
  return present.reduce<string | undefined>((current, value) => {
    if (!current || dayjs(value).isBefore(current)) return value;
    return current;
  }, undefined);
};

const latest = (values: Array<string | undefined>) => {
  const present = values.filter((value): value is string => Boolean(value));
  return present.reduce<string | undefined>((current, value) => {
    if (!current || dayjs(value).isAfter(current)) return value;
    return current;
  }, undefined);
};

const hourlyRate = (row: VesselPortCall) => {
  const started = earliest(
    row.ferryingPeriods.map((period) => period.actualStart),
  );
  if (!started || row.discharged <= 0) return undefined;
  const hours = dayjs().diff(dayjs(started), 'minute') / 60;
  if (hours <= 0) return undefined;
  return Math.round(row.discharged / hours);
};

const estimatedCompletion = (row: VesselPortCall) => {
  const plan = planQuantity(row);
  const remaining = Math.max(plan - row.discharged, 0);
  const rate = hourlyRate(row);
  if (rate && remaining > 0)
    return dayjs()
      .add(remaining / rate, 'hour')
      .toISOString();
  return latest(row.ferryingPeriods.map((period) => period.plannedEnd));
};

const unitVariance = (actual: number | undefined, planned: number) => {
  if (actual == null) return '—';
  const diff = actual - planned;
  if (!planned) return `${diff}`;
  const percent = Math.round((diff / planned) * 1000) / 10;
  const sign = diff > 0 ? '+' : '';
  return `${sign}${diff} (${sign}${percent}%)`;
};

const periodStatusColor = (status?: string) => {
  if (status === 'Completed') return 'green';
  if (status === 'In Progress') return 'blue';
  return 'default';
};

const saveBlob = (data: Blob | undefined, fileName: string) => {
  if (!data) return;
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const Fields = ({
  items,
}: {
  items: { label: string; value?: React.ReactNode }[];
}) => (
  <FieldList>
    {items.map((item) => (
      <FieldRow key={item.label}>
        <dt>{item.label}</dt>
        <dd>{item.value || '—'}</dd>
      </FieldRow>
    ))}
  </FieldList>
);

const QuantityBanner = ({
  label,
  total,
  target,
  matchesLabel,
  differsLabel,
}: {
  label: string;
  total: number;
  target: number;
  matchesLabel: string;
  differsLabel: string;
}) => {
  const matches = target > 0 && total === target;
  return (
    <Banner $ok={matches || target <= 0}>
      {label}: {total.toLocaleString()}
      {target ? ` / ${target.toLocaleString()}` : ''}
      {target ? ` · ${matches ? matchesLabel : differsLabel}` : ''}
    </Banner>
  );
};

const VesselCard = ({
  row,
  location,
  showMap,
}: {
  row: VesselPortCall;
  location?: string;
  showMap: boolean;
}) => {
  const { t } = useTranslate();
  const locations = useLocations();
  const site = (locations.data ?? []).find((item) => item.id === row.siteId);
  const point =
    site?.latitude != null && site.longitude != null
      ? { lat: Number(site.latitude), lng: Number(site.longitude) }
      : undefined;

  return (
    <Panel>
      <VesselHead>
        <VesselMark>
          <Icon icon={faShip} />
        </VesselMark>
        <div>
          <strong>{row.vesselName || '—'}</strong>
          <div>IMO {text(row.imo)}</div>
        </div>
      </VesselHead>
      <Fields
        items={[
          { label: t('location'), value: location },
          { label: t('site'), value: row.site },
          ...(showMap ? [] : [{ label: t('voyage'), value: row.voyage }]),
        ]}
      />
      {showMap && point ? (
        <MapFrame>
          <Map
            center={point}
            zoom={11}
            markers={[
              { ...point, id: `site-${row.siteId}`, content: row.site },
            ]}
            showBorder
          />
        </MapFrame>
      ) : null}
      {showMap && !point ? <Hint>{t('siteLocationUnavailable')}</Hint> : null}
    </Panel>
  );
};

const ActivityTimeline = ({ row }: { row: VesselPortCall }) => {
  const { t } = useTranslate();
  const events = [
    row.ata ? { at: row.ata, text: t('vesselArrivedAtPort') } : undefined,
    ...row.readinessChecks
      .filter((check) => check.isComplete && check.completedAt)
      .map((check) => ({
        at: check.completedAt as string,
        text: t('readinessCheckCompleted', { name: check.name }),
      })),
    ...row.ferryingPeriods.flatMap((period) => {
      const name = period.name || `P${period.periodNumber}`;
      return [
        period.actualStart
          ? {
              at: period.actualStart,
              text: t('ferryingPeriodStarted', { name }),
            }
          : undefined,
        period.actualEnd
          ? {
              at: period.actualEnd,
              text: t('ferryingPeriodCompleted', {
                name,
                count: period.actualUnits ?? 0,
              }),
            }
          : undefined,
      ];
    }),
    row.status === 'Completed' && (row.completedOn || row.atd)
      ? {
          at: (row.completedOn || row.atd) as string,
          text: t('portCallClosedActivity'),
        }
      : undefined,
  ].filter((event): event is { at: string; text: string } => Boolean(event));
  events.sort(
    (left, right) => dayjs(right.at).valueOf() - dayjs(left.at).valueOf(),
  );

  return (
    <Panel>
      <PanelTitle>{t('activityTimeline')}</PanelTitle>
      {events.length ? (
        <ActivityList>
          {events.map((event) => (
            <ActivityItem key={`${event.at}-${event.text}`}>
              <span>{timeConverter(event.at)}</span>
              <Dot />
              <span>{event.text}</span>
            </ActivityItem>
          ))}
        </ActivityList>
      ) : (
        <EmptyState>{t('noActivityYet')}</EmptyState>
      )}
    </Panel>
  );
};

const ReadinessPanel = ({
  checks,
  canOperate,
  checking,
  onToggleCheck,
  start,
  onStart,
}: {
  checks: ReadinessCheck[];
  canOperate: boolean;
  checking?: boolean;
  onToggleCheck: (checkId: number, complete: boolean) => void;
  start?: boolean;
  onStart: () => void;
}) => {
  const { t } = useTranslate();
  const done = checks.filter((check) => check.isComplete).length;
  const mandatoryOpen = checks.some(
    (check) => check.isMandatory && !check.isComplete,
  );
  return (
    <Panel>
      <PanelTitle>
        {t('readinessNextActions')}
        <span>
          {done} / {checks.length}
        </span>
      </PanelTitle>
      {checks.map((check) => (
        <div key={check.id} className="d-flex align-items-center gap-2">
          {checking ? (
            <Spin size="small" />
          ) : (
            <Checkbox
              disabled={!canOperate}
              checked={check.isComplete}
              onChange={() => onToggleCheck(check.id, !check.isComplete)}
            />
          )}
          {check.name}
        </div>
      ))}
      {canOperate ? (
        <div className="d-flex justify-content-center">
          <Button
            type="primary"
            style={{ marginTop: 8 }}
            disabled={mandatoryOpen}
            loading={start}
            onClick={onStart}
            icon={<Icon icon={faPlay} color="#fff" />}
          >
            {t('startDischarge')}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
};

const UpcomingDetail = ({
  row,
  canOperate,
  location,
  onAddPeriod,
  onAutoCreate,
  onDeletePeriod,
  onOpenManifest,
  pending,
}: DetailProps & { location?: string }) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number>();
  const manifest = useShippingManifest(row.manifestId);
  const manifestLoading =
    Boolean(row.manifestId) && (manifest.isPending || manifest.isFetching);
  const detail = manifest.data;
  const references = (detail?.shippingReferences ?? [])
    .map((reference) => reference.reference)
    .filter(Boolean)
    .join(', ');
  const vehicles = detail?.vehicleCount || planQuantity(row) || undefined;
  const plan = planQuantity(row);

  const downloadDocument = async (documentId: number, fileName: string) => {
    setDownloadingDocumentId(documentId);
    try {
      const response = await vessel.downloadShippingManifestDocument(
        row.manifestId as number,
        documentId,
      );
      if (!response.data) {
        message.error(t('generalError'));
        return;
      }
      saveBlob(response.data, fileName);
    } finally {
      setDownloadingDocumentId(undefined);
    }
  };

  return (
    <UpcomingGrid>
      <Panel>
        <PanelTitle>{t('portCallDraft')}</PanelTitle>
        <Subtitle>{t('journeyDetails')}</Subtitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            { label: t('portCall'), children: row.portCallRef },
            {
              label: t('vessel'),
              children: `${row.vesselName || '—'}${row.imo ? ` (${row.imo})` : ''}`,
            },
            { label: t('voyage'), children: row.voyage },
            { label: t('plannedEta'), children: timeConverter(row.eta) },
            { label: t('origin'), children: row.originPort },
            { label: t('destinationSite'), children: row.site },
            {
              label: t('berth'),
              children: row.plannedBerth || row.actualBerth,
            },
            { label: t('ata'), children: timeConverter(row.ata) },
            { label: t('etd'), children: timeConverter(row.etd) },
          ]}
        />
      </Panel>
      <Panel>
        <PanelTitle>
          {t('manifestPlanning')}
          <Tag>{row.manifestRef ? t('planningDraft') : t('notCreated')}</Tag>
        </PanelTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              label: t('manifestDocument'),
              children: row.manifestRef ? (
                <div
                  className="text-link"
                  onClick={() => onOpenManifest?.(row.vesselName)}
                >
                  {row.manifestRef}
                </div>
              ) : (
                t('notCreated')
              ),
            },
            {
              label: t('shippingReference'),
              children: manifestLoading ? <Spin size="small" /> : references,
            },
            {
              label: t('totalVehicles'),
              children: manifestLoading ? (
                <Spin size="small" />
              ) : (
                vehicles?.toLocaleString()
              ),
            },
            {
              label: t('documents'),
              children: manifestLoading ? (
                <Spin size="small" />
              ) : detail?.documents.length ? (
                <Space wrap>
                  {detail.documents.map((document) => (
                    <Tag
                      key={document.id}
                      className="text-link"
                      onClick={() =>
                        void downloadDocument(document.id, document.fileName)
                      }
                    >
                      {downloadingDocumentId === document.id ? (
                        <Spin size="small" />
                      ) : null}{' '}
                      {document.fileName}
                    </Tag>
                  ))}
                </Space>
              ) : undefined,
            },
          ]}
        />
        <Hint>{t('manifestPlanningHint')}</Hint>
      </Panel>
      <Panel>
        <PanelTitle>{t('ferryingPlanDraft')}</PanelTitle>
        {row.ferryingPeriods.length ? (
          <>
            <FerryingTable
              periods={row.ferryingPeriods}
              canOperate={canOperate}
              deleting={pending?.deletePeriod}
              onDeletePeriod={onDeletePeriod}
            />
            <QuantityBanner
              label={t('totalFerryingUnits')}
              total={row.ferryingPlannedUnits}
              target={plan}
              matchesLabel={t('matchesVesselQuantity')}
              differsLabel={t('doesNotMatchVesselQuantity')}
            />
          </>
        ) : (
          <EmptyState>
            <strong>{t('noFerryingPeriods')}</strong>
            <Hint>{t('ferryingPlanHint')}</Hint>
          </EmptyState>
        )}
        {canOperate ? (
          <Actions>
            {!row.ferryingPeriods.length ? (
              <Button
                type="primary"
                loading={pending?.savePeriod}
                onClick={onAutoCreate}
              >
                {t('autoCreatePlan')}
              </Button>
            ) : null}
            <Button icon={<Icon icon={faPlus} />} onClick={onAddPeriod}>
              {t('addFerryingPeriod')}
            </Button>
          </Actions>
        ) : null}
      </Panel>
      <VesselCard row={row} location={location} showMap />
    </UpcomingGrid>
  );
};

const FerryingTable = ({
  periods,
  canOperate,
  deleting,
  onDeletePeriod,
  showActual,
}: {
  periods: FerryingPeriod[];
  canOperate: boolean;
  deleting?: boolean;
  onDeletePeriod: (period: FerryingPeriod) => void;
  showActual?: boolean;
}) => {
  const { t } = useTranslate();
  return (
    <Table<FerryingPeriod>
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={periods}
      columns={[
        { title: t('period'), dataIndex: 'name', width: 70, fixed: 'left' },
        {
          title: t('plannedStart'),
          dataIndex: 'plannedStart',
          width: 150,
          render: (value: string) => timeConverter(value),
        },
        {
          title: t('plannedEnd'),
          dataIndex: 'plannedEnd',
          width: 150,
          render: (value: string) => timeConverter(value),
        },
        ...(showActual
          ? [
              {
                title: t('actualStart'),
                dataIndex: 'actualStart' as const,
                width: 150,
                render: (value?: string) => timeConverter(value),
              },
              {
                title: t('actualEnd'),
                dataIndex: 'actualEnd' as const,
                width: 150,
                render: (value?: string) => timeConverter(value),
              },
              {
                title: t('unitsPlan'),
                dataIndex: 'plannedUnits' as const,
                width: 90,
              },
              {
                title: t('unitsActual'),
                dataIndex: 'actualUnits' as const,
                width: 90,
                render: (value?: number) => value ?? '—',
              },
              {
                title: t('variance'),
                dataIndex: 'actualUnits' as const,
                render: (_: unknown, period: FerryingPeriod) =>
                  unitVariance(period.actualUnits, period.plannedUnits),
              },
            ]
          : [
              {
                title: t('units'),
                dataIndex: 'plannedUnits' as const,
                width: 80,
              },
            ]),
        ...(canOperate
          ? [
              {
                title: t('actions'),
                width: 70,
                align: 'center' as const,
                fixed: 'right' as const,
                render: (_: unknown, period: FerryingPeriod) => (
                  <Popconfirm
                    title={t('delete')}
                    description={period.name || `P${period.periodNumber}`}
                    okButtonProps={{ danger: true, loading: deleting }}
                    onConfirm={() => onDeletePeriod(period)}
                  >
                    <TooltipIcon
                      title={t('delete')}
                      icon={faTrash}
                      color="#ff4d4f"
                    />
                  </Popconfirm>
                ),
              },
            ]
          : []),
      ]}
      scroll={{ x: 'max-content' }}
    />
  );
};

const FerryingTrack = ({ periods }: { periods: FerryingPeriod[] }) => {
  if (!periods.length) return null;
  const start = earliest(periods.map((period) => period.plannedStart));
  const end = latest(periods.map((period) => period.plannedEnd));
  if (!start || !end) return null;
  const span = Math.max(dayjs(end).valueOf() - dayjs(start).valueOf(), 1);
  return (
    <Track>
      {periods.map((period) => {
        const left =
          ((dayjs(period.plannedStart).valueOf() - dayjs(start).valueOf()) /
            span) *
          100;
        const width = Math.max(
          ((dayjs(period.plannedEnd).valueOf() -
            dayjs(period.plannedStart).valueOf()) /
            span) *
            100,
          8,
        );
        return (
          <TrackBlock
            key={period.id}
            style={{
              left: `${left}%`,
              width: `${Math.min(width, 100 - left)}%`,
            }}
          >
            {period.name || `P${period.periodNumber}`}
          </TrackBlock>
        );
      })}
    </Track>
  );
};

const InPortDetail = (props: DetailProps & { location?: string }) => {
  const { t } = useTranslate();
  const { row, canOperate, location, onAddPeriod, onDeletePeriod, onStart } =
    props;
  const plan = planQuantity(row);
  return (
    <InPortGrid>
      <Panel>
        <PanelTitle>{t('portCall')}</PanelTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            { label: t('portCall'), children: row.portCallRef },
            {
              label: t('vessel'),
              children: `${row.vesselName || '—'}${row.imo ? ` (${row.imo})` : ''}`,
            },
            { label: t('voyage'), children: row.voyage },
            { label: t('siteTerminal'), children: row.site },
            {
              label: t('berth'),
              children: row.plannedBerth || row.actualBerth,
            },
            { label: t('etaUtc'), children: timeConverter(row.eta) },
            { label: t('ata'), children: timeConverter(row.ata) },
            { label: t('agent'), children: row.agent },
            {
              label: t('destinationCompound'),
              children: row.destinationCompound,
            },
            { label: t('customsCleared'), children: undefined },
          ]}
        />
      </Panel>
      <Panel>
        <PanelTitle>{t('ferryingPlan')}</PanelTitle>
        <FerryingTrack periods={row.ferryingPeriods} />
        <FerryingTable
          periods={row.ferryingPeriods}
          canOperate={canOperate}
          deleting={props.pending?.deletePeriod}
          onDeletePeriod={onDeletePeriod}
        />
        {canOperate ? (
          <Button
            style={{ marginTop: 8 }}
            icon={<Icon icon={faPlus} />}
            onClick={onAddPeriod}
          >
            {t('addFerryingPeriod')}
          </Button>
        ) : null}
        <QuantityBanner
          label={t('totalFerryingUnits')}
          total={row.ferryingPlannedUnits}
          target={plan}
          matchesLabel={t('matchesVesselQuantity')}
          differsLabel={t('doesNotMatchVesselQuantity')}
        />
      </Panel>
      <Stack>
        <VesselCard row={row} location={location} showMap />
        <ReadinessPanel
          checks={row.readinessChecks}
          canOperate={canOperate}
          checking={props.pending?.check}
          onToggleCheck={props.onToggleCheck}
          start={props.pending?.start}
          onStart={onStart}
        />
      </Stack>
    </InPortGrid>
  );
};

const DischargingDetail = (props: DetailProps & { location?: string }) => {
  const { t } = useTranslate();
  const { row, canOperate, location, onClose } = props;
  const plan = planQuantity(row);
  const percent = plan > 0 ? Math.round((row.discharged / plan) * 100) : 0;
  const rate = hourlyRate(row);
  const completion = estimatedCompletion(row);
  const commenced = earliest(
    row.ferryingPeriods.map((period) => period.actualStart),
  );
  const labour = row.ferryingPeriods
    .map((period) => period.teamOrLane)
    .filter(Boolean)
    .join(', ');
  const remaining = Math.max(plan - row.discharged, 0);
  const hoursLeft = completion
    ? Math.max(dayjs(completion).diff(dayjs(), 'hour', true), 0)
    : undefined;
  const blockers = row.readinessChecks.filter((check) => !check.isComplete);

  return (
    <ThreeColumnGrid>
      <Panel>
        <PanelTitle>
          {t('dischargeProgress')}
          <Tag color="green">{t('liveBadge')}</Tag>
        </PanelTitle>
        <Progress percent={percent} />
        <div>
          {row.discharged.toLocaleString()} / {plan.toLocaleString()}
        </div>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              label: t('currentHourlyRate'),
              children: rate
                ? t('vehiclesPerHour', { count: rate })
                : undefined,
            },
            {
              label: t('estimatedCompletion'),
              children: timeConverter(completion),
            },
            { label: t('labourTeams'), children: labour },
            {
              label: t('dischargeCommenced'),
              children: timeConverter(commenced),
            },
          ]}
        />
        {canOperate ? (
          <Popconfirm title={t('closePortCall')} onConfirm={onClose}>
            <Button
              style={{ marginTop: 8 }}
              block
              loading={props.pending?.close}
            >
              {t('closePortCall')}
            </Button>
          </Popconfirm>
        ) : null}
      </Panel>
      <Panel>
        <PanelTitle>{t('activeFerryingPeriod')}</PanelTitle>
        <FerryingTrack periods={row.ferryingPeriods} />
        <Table<FerryingPeriod>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={row.ferryingPeriods}
          columns={[
            { title: t('period'), dataIndex: 'name', width: 70 },
            { title: t('plannedUnits'), dataIndex: 'plannedUnits' },
            {
              title: t('actualUnits'),
              dataIndex: 'actualUnits',
              render: (value?: number) => value ?? 0,
            },
            {
              title: t('remaining'),
              dataIndex: 'plannedUnits',
              render: (_: unknown, period) =>
                Math.max(period.plannedUnits - (period.actualUnits ?? 0), 0),
            },
            {
              title: t('status'),
              dataIndex: 'status',
              render: (value?: string) => (
                <Tag color={periodStatusColor(value)}>{value || '—'}</Tag>
              ),
            },
          ]}
        />
      </Panel>
      <Panel>
        <PanelTitle>
          {t('exceptions')}
          {blockers.length ? <Tag>{blockers.length}</Tag> : null}
        </PanelTitle>
        {blockers.length ? (
          blockers.map((check) => (
            <div key={check.id} style={{ marginBottom: 8 }}>
              <Tag>{check.name}</Tag>
              {canOperate ? (
                <Button
                  size="small"
                  type="link"
                  loading={props.pending?.check}
                  disabled={props.pending?.check}
                  onClick={() => props.onToggleCheck(check.id, true)}
                >
                  {t('complete')}
                </Button>
              ) : null}
            </div>
          ))
        ) : (
          <EmptyState>{t('noOpenExceptions')}</EmptyState>
        )}
      </Panel>
      <VesselCard row={row} location={location} showMap={false} />
      <Panel>
        <PanelTitle>{t('forecastRemaining')}</PanelTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              label: t('totalVehicles'),
              children: plan ? plan.toLocaleString() : undefined,
            },
            {
              label: t('discharged'),
              children: `${row.discharged.toLocaleString()}${plan ? ` (${percent}%)` : ''}`,
            },
            { label: t('remaining'), children: remaining.toLocaleString() },
            {
              label: t('currentHourlyRate'),
              children: rate
                ? t('vehiclesPerHour', { count: rate })
                : undefined,
            },
            {
              label: t('estimatedCompletion'),
              children: timeConverter(completion),
            },
            {
              label: t('hoursRemaining'),
              children:
                hoursLeft != null ? `${Math.round(hoursLeft)}h` : undefined,
            },
          ]}
        />
      </Panel>
      <ActivityTimeline row={row} />
    </ThreeColumnGrid>
  );
};

const CompletedDetail = ({
  row,
  onOpenManifest,
}: {
  row: VesselPortCall;
  onOpenManifest?: (vesselName?: string) => void;
}) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number>();
  const manifest = useShippingManifest(row.manifestId);
  const manifestLoading =
    Boolean(row.manifestId) && (manifest.isPending || manifest.isFetching);
  const detail = manifest.data;
  const plan = planQuantity(row);
  const varianceMinutes =
    row.etd && row.atd
      ? dayjs(row.atd).diff(dayjs(row.etd), 'minute')
      : undefined;
  const varianceLabel =
    varianceMinutes == null
      ? '—'
      : varianceMinutes === 0
        ? t('onPlan')
        : `${durationText(row.etd, row.atd)} ${varianceMinutes < 0 ? t('aheadOfPlan') : t('behindPlan')}`;
  const matches = plan > 0 && row.discharged === plan;

  const downloadDocument = async (documentId: number, fileName: string) => {
    setDownloadingDocumentId(documentId);
    try {
      const response = await vessel.downloadShippingManifestDocument(
        row.manifestId as number,
        documentId,
      );
      if (!response.data) {
        message.error(t('generalError'));
        return;
      }
      saveBlob(response.data, fileName);
    } finally {
      setDownloadingDocumentId(undefined);
    }
  };

  return (
    <CompletedGrid>
      <Panel>
        <PanelTitle>{t('completionSummary')}</PanelTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            { label: t('portCall'), children: row.portCallRef },
            {
              label: t('vessel'),
              children: `${row.vesselName || '—'}${row.imo ? ` (${row.imo})` : ''}`,
            },
            { label: t('siteTerminal'), children: row.site },
            { label: t('arrivedAt'), children: timeConverter(row.ata) },
            {
              label: t('completionTime'),
              children: timeConverter(row.completedOn || row.atd),
            },
            {
              label: t('plannedVehicles'),
              children: plan ? plan.toLocaleString() : undefined,
            },
            {
              label: t('dischargedVehicles'),
              children: row.discharged.toLocaleString(),
            },
            {
              label: t('turnaroundDuration'),
              children: durationText(row.ata, row.atd),
            },
            {
              label: t('plannedBerthWindow'),
              children: windowText(row.eta, row.etd),
            },
            {
              label: t('actualBerthWindow'),
              children: windowText(row.ata, row.atd),
            },
            { label: t('varianceFromPlan'), children: varianceLabel },
          ]}
        />
      </Panel>
      <Panel>
        <PanelTitle>{t('ferryingPeriodPerformance')}</PanelTitle>
        <FerryingTable
          periods={row.ferryingPeriods}
          canOperate={false}
          onDeletePeriod={() => undefined}
          showActual
        />
        <QuantityBanner
          label={t('totalFerryingCompleted')}
          total={row.discharged}
          target={plan}
          matchesLabel={t('matchesPlannedQuantity')}
          differsLabel={`${t('variance')}: ${unitVariance(row.discharged, plan)}`}
        />
      </Panel>
      <Panel>
        <PanelTitle>{t('reconciliationOutcome')}</PanelTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              label: t('vehiclesDischargedOutcome'),
              children: `${row.discharged.toLocaleString()} / ${plan.toLocaleString()}`,
            },
            ...row.readinessChecks.map((check) => ({
              label: check.name,
              children: check.isComplete ? t('complete') : t('notComplete'),
            })),
          ]}
        />
        <Banner $ok={matches}>
          {matches
            ? t('noDiscrepancies')
            : `${t('variance')}: ${unitVariance(row.discharged, plan)}`}
        </Banner>
      </Panel>
      <Panel>
        <PanelTitle>{t('documents')}</PanelTitle>
        {row.manifestRef ? (
          <div>
            <Button
              type="link"
              size="small"
              onClick={() => onOpenManifest?.(row.vesselName)}
            >
              {row.manifestRef}
            </Button>
          </div>
        ) : null}
        {manifestLoading ? (
          <Spin size="small" />
        ) : detail?.documents.length ? (
          detail.documents.map((document) => (
            <div key={document.id}>
              <Button
                type="link"
                size="small"
                loading={downloadingDocumentId === document.id}
                onClick={() =>
                  void downloadDocument(document.id, document.fileName)
                }
              >
                {document.fileName}
              </Button>
            </div>
          ))
        ) : (
          <EmptyState>{t('noDocumentsAttached')}</EmptyState>
        )}
      </Panel>
      <ActivityTimeline row={row} />
    </CompletedGrid>
  );
};

export const PortCallDetail = (props: DetailProps) => {
  const frame = useRef<HTMLDivElement>(null);
  const width = useFitWidth(frame);
  const positions = useVesselPositions();
  const position = (positions.data?.entities ?? []).find(
    (item) => item.vesselId === props.row.vesselId,
  );
  const location = position?.currentLocationName || position?.lastPort;
  let detail = <UpcomingDetail {...props} location={location} />;
  if (props.row.status === 'InPort') {
    detail = <InPortDetail {...props} location={location} />;
  } else if (props.row.status === 'Discharging') {
    detail = <DischargingDetail {...props} location={location} />;
  } else if (props.row.status === 'Completed') {
    detail = (
      <CompletedDetail row={props.row} onOpenManifest={props.onOpenManifest} />
    );
  }
  return (
    <DetailFrame ref={frame} $width={width}>
      {detail}
    </DetailFrame>
  );
};
